/** Which sockets an artifact may sit in.
 *
 * The classic has five miscellaneous sockets and two ring fingers, and any
 * miscellaneous artifact fits any of the five - the table names a slot per
 * artifact only because a def has to say which KIND of thing it is. Before
 * this module the kernel read that name as the one socket the artifact could
 * ever occupy, and since fifty-six defs say `misc1` and thirteen say
 * `leftRing`, a hero could wear one trinket and one ring, full stop: the four
 * other miscellaneous sockets on the doll never received anything, and a
 * second Four-Leaf Clover sat in the bag doing nothing.
 *
 * A *family* is the set of interchangeable sockets. Everything that asks "may
 * this go there" or "where does this go" asks here, so the enumerator, the
 * executor, assembly and the sheet cannot disagree.
 */

import type { ArtifactDef, ArtifactSlot } from "../content/registry.js";
import type { HeroState } from "../state.js";
import type { ArtifactDefId } from "../ids.js";

export const MISC_SLOTS: readonly ArtifactSlot[] =
  Object.freeze(["misc1", "misc2", "misc3", "misc4", "misc5"]);
export const RING_SLOTS: readonly ArtifactSlot[] = Object.freeze(["leftRing", "rightRing"]);

/** Every socket this artifact could occupy, in the order a free one is taken. */
export function slotsFor(def: Pick<ArtifactDef, "slot">): readonly ArtifactSlot[] {
  if (MISC_SLOTS.includes(def.slot)) return MISC_SLOTS;
  if (RING_SLOTS.includes(def.slot)) return RING_SLOTS;
  return [def.slot];
}

/** Whether `slot` is one of the sockets this artifact fits. */
export function slotAccepts(def: Pick<ArtifactDef, "slot">, slot: string): boolean {
  return (slotsFor(def) as readonly string[]).includes(slot);
}

export function slotIsFree(h: HeroState, slot: string): boolean {
  const cur = h.artifacts[slot];
  return cur === null || cur === undefined;
}

/** The first empty socket this artifact fits, or null when its family is full. */
export function freeSlotFor(h: HeroState, def: Pick<ArtifactDef, "slot">): ArtifactSlot | null {
  for (const s of slotsFor(def)) if (slotIsFree(h, s)) return s;
  return null;
}

/**
 * The doll with some sockets rewritten: `changes` maps a slot to what now sits
 * in it (null empties it). Every other socket is carried over unchanged and
 * the keys come out sorted, so the record is the same bytes whichever path
 * built it. This is the one way the kernel changes `HeroState.artifacts`:
 * equip, unequip, pickup, assembly and a quest's toll all go through it, so
 * a rule that has to run beside such a change (mana retune, see
 * `retuneMana`) has one place to stand.
 */
export function withSlots(
  h: HeroState, changes: Readonly<Record<string, ArtifactDefId | null>>,
): HeroState["artifacts"] {
  const out: Record<string, ArtifactDefId | null> = {};
  for (const k of [...Object.keys(h.artifacts), ...Object.keys(changes)].sort()) {
    out[k] = k in changes ? changes[k] ?? null : h.artifacts[k] ?? null;
  }
  return out;
}

/** Where the hero wears this artifact, or null when it is not on the doll. */
export function wornSlotOf(h: HeroState, art: ArtifactDefId): string | null {
  for (const s of Object.keys(h.artifacts).sort()) if (h.artifacts[s] === art) return s;
  return null;
}
